"use client";

import Link from "next/link";
import { menus } from "./constans";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ArrowRight, Dismiss, Hamburger } from "@/components/icons";
import { Fragment, useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { Link as LinkScroll } from "react-scroll";
import { usePathname } from "next/navigation";
import Enroll from "../modal/enroll";
// import { useChat } from "@/context/ChatContext";

const Header = ({
  className,
  session: propsSession,
  status: propsStatus,
}: {
  className?: string;
  session?: any;
  status?: string;
}) => {
  const { data: clientSession, status: clientStatus }: any = useSession();

  const [status, setStatus] = useState<string>(propsStatus ?? "loading");
  const [currentUser, setCurrentUser] = useState<any>(
    propsSession?.user ?? null
  );

  useEffect(() => {
    if (clientStatus === "loading") return; // wait until session resolves
    setStatus(clientStatus);
    setCurrentUser(clientSession?.user ?? null);
  }, [clientStatus, clientSession]);

  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const isAuthenticated = status === "authenticated";

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };

    const handleResize = () => {
      window.requestAnimationFrame(() => {
        if (window.innerWidth >= 1024) setOpen(false);
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <header
      className={cn(
        "fixed top-0 z-30 w-full bg-transparent transition-all duration-300",
        isScrolled && "bg-primary shadow-md",
        className
      )}
    >
      <div className="relative container flex items-center justify-between py-4">
        <Link href="/" className="max-w-40 cursor-pointer lg:max-w-64">
          <Image
            src="/logo.webp"
            alt="Company Logo"
            width={160}
            height={24}
            className="h-auto w-full"
            priority
          />
        </Link>

        {/* Desktop nav — centered */}
        <div className="absolute top-1/2 left-1/2 hidden -translate-x-1/2 -translate-y-1/2 gap-6 lg:flex">
          {menus.map((menu) => (
            <Fragment key={menu.href}>
              {menu.label === "FAQs" ? (
                <Link
                  href="/#faq"
                  className="group relative px-2 py-3 text-xl font-semibold text-white"
                >
                  FAQs
                  <span
                    className={cn(
                      "absolute bottom-2 left-0 h-0.5 w-0 bg-slate-300 transition-all duration-300 group-hover:w-full",
                      { "w-full": pathname === menu.href }
                    )}
                  />
                </Link>
              ) : (
                <Link
                  href={menu.href}
                  className="group relative px-2 py-3 text-xl font-semibold text-white"
                >
                  {menu.label}
                  <span
                    className={cn(
                      "absolute bottom-2 left-0 h-0.5 w-0 bg-slate-300 transition-all duration-300 group-hover:w-full",
                      { "w-full": pathname === menu.href }
                    )}
                  />
                </Link>
              )}
            </Fragment>
          ))}
          {/* <div
              onClick={() => setChatModal(true)}
              className="text-white text-base lg:text-xl font-semibold px-2 py-2 lg:py-3 relative group cursor-pointer"
            >
              Contact Us
              <span className="absolute left-0 bottom-2 h-0.5 bg-slate-300 w-0 transition-all duration-300 group-hover:w-full"></span>
            </div> */}
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-4 lg:gap-8">
          {/* Login / My Account */}
          {pathname !== "/login" && (
            <>
              {isAuthenticated ? (
                <Link
                  href="/my-course"
                  className="group relative px-2 py-2 text-base font-semibold text-white lg:py-3 lg:text-xl"
                >
                  My Account
                  <span className="absolute bottom-2 left-0 h-0.5 w-0 bg-slate-300 transition-all duration-300 group-hover:w-full" />
                </Link>
              ) : (
                <Link
                  href="/login"
                  className="group relative px-2 py-2 text-base font-semibold text-white lg:py-3 lg:text-xl"
                >
                  Login
                  <span className="absolute bottom-2 left-0 h-0.5 w-0 bg-slate-300 transition-all duration-300 group-hover:w-full" />
                </Link>
              )}
            </>
          )}

          {/* Enroll CTA — desktop only */}
          {!isAuthenticated && (
            <div className="hidden lg:flex">
              <Enroll>
                <Button
                  className="text-primary rounded-full bg-[#FFBB00] hover:bg-[#FFBB00]/90 has-[>svg]:px-6"
                  size="2xl"
                >
                  Start Training Today
                  <ArrowRight className="text-900 ml-2 size-6 shrink-0 lg:ml-3" />
                </Button>
              </Enroll>
            </div>
          )}

          {/* Mobile hamburger */}
          <div
            className="flex size-10 cursor-pointer items-center justify-center rounded-lg bg-slate-100 p-2.5 lg:hidden"
            onClick={() => setOpen(!open)}
          >
            {open ? (
              <Dismiss className="text-xl text-slate-900" />
            ) : (
              <Hamburger className="text-xl text-slate-900" />
            )}
          </div>
        </div>

        {/* Mobile drawer */}
        {open && (
          <>
            <div
              className="fixed top-[76px] left-0 z-40 h-[calc(100%-76px)] w-full bg-slate-900/80"
              onClick={() => setOpen(false)}
            />
            <div className="fixed top-[72px] left-0 z-50 flex h-fit w-full flex-col bg-white lg:hidden">
              {menus.map((menu) => (
                <Fragment key={menu.href}>
                  {menu.label === "FAQs" && pathname === "/" ? (
                    <LinkScroll
                      to="faq"
                      smooth
                      duration={500}
                      spy
                      offset={-100}
                      className="w-full cursor-pointer border-b border-slate-200 px-5 py-3 text-base font-medium text-slate-900"
                      onClick={() => setOpen(false)}
                    >
                      FAQs
                    </LinkScroll>
                  ) : (
                    <Link
                      href={
                        menu.label === "FAQs"
                          ? currentUser
                            ? "/faqs"
                            : "/faqs-list"
                          : menu.href
                      }
                      className="w-full border-b border-slate-200 px-5 py-3 text-base font-medium text-slate-900"
                      onClick={() => setOpen(false)}
                    >
                      {menu.label}
                    </Link>
                  )}
                </Fragment>
              ))}
              {/* <div
                    onClick={() => setChatModal(true)}
                    className="text-base font-medium text-slate-900 py-3 px-5 w-full border-b border-slate-200"
                  >
                    Contact Us
                    <span className="absolute left-0 bottom-2 h-0.5 bg-slate-300 w-0 transition-all duration-300 group-hover:w-full"></span>
                  </div> */}

              {!isAuthenticated && (
                <Enroll>
                  <Button
                    className="text-900 mx-auto my-3 w-full max-w-[350px] rounded-full bg-[#FFBB00] hover:bg-[#FFBB00]/90"
                    size="xl"
                  >
                    Start Training Today
                    <ArrowRight className="text-900 ml-2 size-6 shrink-0 lg:ml-3" />
                  </Button>
                </Enroll>
              )}
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
